import React, { forwardRef, useEffect, useRef } from 'react'

import { mergeRefs } from 'utils/ref_utils'

interface FocusTrapProps extends React.HTMLAttributes<HTMLDivElement> {
  active?: boolean;
  children?: React.ReactNode;
  setInitialFocusOn?: HTMLElement | string | React.RefObject<HTMLElement>
}

function isScrollable (element: HTMLElement) {
  const style = getComputedStyle(element)
  const allowsOverflow = /(scroll|auto)/.test(style.overflow + style.overflowX + style.overflowY)

  return allowsOverflow && element.scrollHeight > element.clientHeight
}

export function getAllFocusableElements (element: HTMLElement) {
  const candidates: HTMLElement[] = []
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node: HTMLElement) => {
      const isHiddenInput = node instanceof HTMLInputElement && node.type === 'hidden'
      const isLinkWithoutHref = node instanceof HTMLAnchorElement && !node.href

      if (
        node.hidden ||
        isHiddenInput ||
        isLinkWithoutHref ||
        node.matches(':disabled')
      ) {
        return NodeFilter.FILTER_SKIP
      }

      if (
        node.tabIndex >= 0 ||
        node.contentEditable === 'true' ||
        isScrollable(node)
      ) {
        return NodeFilter.FILTER_ACCEPT
      }

      return NodeFilter.FILTER_SKIP
    }
  })

  while (walker.nextNode()) {
    candidates.push(walker.currentNode as HTMLElement)
  }

  return candidates
}

function isRefWithHTMLElement (value: unknown): value is React.RefObject<HTMLElement> {
  return (
    value instanceof Object &&
    'current' in value &&
    value.current instanceof HTMLElement
  )
}

function getInitialFocusElement (
  element: HTMLElement,
  setInitialFocusOn: FocusTrapProps['setInitialFocusOn']
) {
  if (typeof setInitialFocusOn === 'string') {
    return element.querySelector(setInitialFocusOn) as HTMLElement | null
  }

  if (setInitialFocusOn instanceof HTMLElement && element.contains(setInitialFocusOn)) {
    return setInitialFocusOn
  }

  if (isRefWithHTMLElement(setInitialFocusOn) && element.contains(setInitialFocusOn.current)) {
    return setInitialFocusOn.current
  }
}

export const FocusTrap = forwardRef<HTMLDivElement, FocusTrapProps>(({ active = true, children, setInitialFocusOn, ...props }, forwardedRef) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const trapElement = ref.current
    if (!trapElement || !active) return

    const focusableElements = getAllFocusableElements(trapElement)
    const firstFocusableElement = getInitialFocusElement(
      trapElement,
      setInitialFocusOn
    ) || focusableElements[0]

    const previousFocusedElement = document.activeElement

    if (firstFocusableElement) {
      firstFocusableElement.focus({ preventScroll: true })
    } else {
      ref.current.focus({
        preventScroll: true
      })
    }

    return () => {
      if (previousFocusedElement) {
        // If the active element is not the `body` element and not within the
        // trap element at this point, we take that as an indication that either
        // a) something outside of this component has programmatically moved the
        // focus or b) the user used a pointer device (e.g. a mouse) to interact
        // outside of the trap. In these cases, we don't want to interfere with
        // the document's active element.
        if (
          !trapElement.contains(document.activeElement) &&
          document.activeElement !== document.body
        ) {
          return
        }

        (previousFocusedElement as HTMLElement).focus({
          preventScroll: true,
        })
      }
    }
  }, [active])

  return (
    <>
      <div
        aria-hidden
        data-focus-guard=''
        onFocus={(event) => {
          if (ref.current) {
            const focusableElements = getAllFocusableElements(ref.current)
            const lastFocusableElement = focusableElements[focusableElements.length - 1]

            if (lastFocusableElement) {
              lastFocusableElement.focus()
            } else {
              ref.current.focus()
            }

            event.stopPropagation()
          }
        }}
        tabIndex={active ? 0 : undefined}
      />
      <div {...props} ref={mergeRefs(ref, forwardedRef)} tabIndex={active ? -1 : undefined}>
        {children}
      </div>
      <div
        aria-hidden
        data-focus-guard=''
        onFocus={(event) => {
          if (ref.current) {
            const focusableElements = getAllFocusableElements(ref.current)
            const firstFocusableElement = focusableElements[0]

            if (firstFocusableElement) {
              firstFocusableElement.focus()
            } else {
              ref.current.focus()
            }
          }

          event.stopPropagation()
        }}
        tabIndex={active ? 0 : undefined}
      />
    </>
  )
})
